import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Panel, PanelConfig, Door, Drain, MiddleWall, Hari, HariCorner, ToolType, CeilingPanel } from '../types';
import { PolyPanel, Pt } from '../engine/polyEngine';
import { drawPolyPanels, drawOutline, drawExistingWalls } from '../engine/polyRender';
import { Opening } from '../engine/polyEngine';
import { calculateMiddleWallPanels } from '../utils/panelLayout';

// 하리 도면 좌표 기준 사각형 계산 (벽 바깥)
// 코너형: 벽 교차 코너 바깥 영역
// 중간형: 해당 벽 바깥 (벽 외부로 돌출)
function getHariRect(hari: Hari, horizontal: number, vertical: number, panelThick: number): { x: number; y: number; w: number; h: number } | null {
  const pt = panelThick;
  if (hari.hariType === 'corner') {
    const c = hari.corner;
    // 코너 바깥: 벽 두께(pt) 영역 안에 그림 (두 벽이 겹치는 코너 사각형)
    if (c === 'topLeft')     return { x: 0,                    y: 0,                   w: hari.width,  h: hari.height };
    if (c === 'topRight')    return { x: horizontal - hari.width, y: 0,                w: hari.width,  h: hari.height };
    if (c === 'bottomLeft')  return { x: 0,                    y: vertical - hari.height, w: hari.width, h: hari.height };
    if (c === 'bottomRight') return { x: horizontal - hari.width, y: vertical - hari.height, w: hari.width, h: hari.height };
  } else {
    const ws = hari.wallSide;
    const pos = hari.position ?? 0;
    const posOffset = (ws === 'top' || ws === 'right') ? pt : 0;
    // 중간형: 벽 바깥으로 돌출 (height = 돌출 깊이)
    if (ws === 'top' || ws === 'bottom') {
      return {
        x: posOffset + pos,
        y: ws === 'top' ? 0 : vertical - hari.height,
        w: hari.width, h: hari.height,
      };
    } else {
      return {
        x: ws === 'left' ? 0 : horizontal - hari.width,
        y: posOffset + pos,
        w: hari.width, h: hari.height,
      };
    }
  }
  return null;
}

/** 문 끌기 안내 (세계 좌표: 가로 면 x, 세로 면 y) */
export interface DoorGuide {
  id: string;
  side: 'left' | 'right' | 'top' | 'bottom';
  from: number; to: number;            // 문 구간
  slot: { lo: number; hi: number };    // 문이 놓인 빈 구간 (벽 안쪽 면·하리·다른 문 사이)
  snap?: string;                       // 자석 맞춤 설명 (예: 왼쪽 900×2 맞춤)
  pieceLo?: number; pieceHi?: number;  // 양옆 패널 조각 길이
}

interface DrawingCanvasProps {
  horizontal: number;
  vertical: number;
  walls: {
    left: Panel[];
    right: Panel[];
    top: Panel[];
    bottom: Panel[];
  };
  config: PanelConfig;
  panelThick: number;
  doors: Door[];
  drains: Drain[];
  activeTool: ToolType;
  ceilingPanels?: CeilingPanel[];
  ceilingDirection?: 'H' | 'V';
  onWallClick?: (wallSide: 'left' | 'right' | 'top' | 'bottom', position: number) => void;
  onFloorClick?: (x: number, y: number) => void;
  onObjectClick?: (type: 'door' | 'drain', id: string) => void;
  onPanelClick?: (wallSide: 'left' | 'right' | 'top' | 'bottom', panelIndex: number) => void;
  onCeilingPanelClick?: (index: number) => void;
  onDoorClick?: (doorId: string) => void;
  onDrainMove?: (id: string, x: number, y: number) => void;
  onDrainClick?: (drainId: string) => void;
  onDrainDistanceEdit?: (id: string, dir: 'top' | 'bottom' | 'left' | 'right', value: number) => void;
  middleWalls?: MiddleWall[];
  onMiddleWallClick?: (id: string) => void;
  onMiddleWallMove?: (id: string, position: number) => void;
  haris?: Hari[];
  onHariPlace?: (x: number, y: number) => void;
  onHariClick?: (id: string) => void;
  // 다각형 엔진 결과 — 있으면 벽·하리 패널과 외곽선·패널 치수를 이것으로 그림
  poly?: { panels: PolyPanel[]; outline: Pt[] } | null;
  onPolyPanelClick?: (id: string) => void;
  selectedPolyId?: string | null;
  existingWalls?: Opening[];                                 // 기존 벽 (빗금 표시)
  doorEnds?: Record<string, { lo: 'yangsu' | 'eumsu'; hi: 'yangsu' | 'eumsu' }>; // 엔진이 정한 문 양 끝 (화면 왼/위 = lo)
  onEdgeClick?: (edgeKey: string, from: number, to: number) => void; // 기존벽 도구로 변 클릭
  onDoorDrag?: (id: string, position: number, phase: 'move' | 'end', snapMM: number) => void; // 문 끌기 (로컬 위치)
  doorGuide?: DoorGuide | null;
  panelNos?: Record<string, string> | null; // 있으면 패널 안 글자를 사이즈 대신 번호로 (천장은 C01…)
  exportRef?: React.MutableRefObject<((w: number, h: number) => HTMLCanvasElement) | null>; // 인쇄용 그림 받기 (화면과 같은 그림)
}

const PADDING = 120;
const horizOrVert = (side: string) => (side === 'top' || side === 'bottom' ? 'ew-resize' : 'ns-resize');
const DIM_OFFSET = 48;   // 패널별 치수선 오프셋
const DIM_OFFSET2 = 90;  // 전체 치수선 오프셋
const FONT_SIZE = 11;

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  horizontal, vertical, walls, config, panelThick,
  doors, drains, activeTool,
  ceilingPanels = [], ceilingDirection = 'H',
  onWallClick, onFloorClick, onObjectClick, onPanelClick, onCeilingPanelClick, onDoorClick,
  onDrainMove, onDrainClick, onDrainDistanceEdit,
  middleWalls = [], onMiddleWallClick, onMiddleWallMove,
  haris = [], onHariPlace, onHariClick,
  poly,
  onPolyPanelClick, selectedPolyId, existingWalls = [], onEdgeClick, doorEnds,
  onDoorDrag, doorGuide, exportRef, panelNos,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const viewOffset = offset;
  const [sizeTick, setSizeTick] = useState(0); // 화면 크기 바뀜 신호
  const [isPanning, setIsPanning] = useState(false);
  const [hoverWall, setHoverWall] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const offsetStart = useRef({ x: 0, y: 0 });
  const transformRef = useRef({ tx: 0, ty: 0, scale: 1 });

  // 배수구 드래그 상태
  const [draggingDrain, setDraggingDrain] = useState<{ id: string; x: number; y: number } | null>(null);
  const draggingDrainRef = useRef<{ id: string; x: number; y: number } | null>(null);
  const drainDragStart = useRef<{ clientX: number; clientY: number; drawX: number; drawY: number } | null>(null);

  // 칸막이 드래그 상태
  const [draggingMW, setDraggingMW] = useState<{ id: string; position: number } | null>(null);
  const draggingMWRef = useRef<{ id: string; position: number } | null>(null);
  const mwDragStart = useRef<{ clientX: number; clientY: number; startPos: number; dir: 'horizontal'|'vertical' } | null>(null);

  // 배수구 거리 라벨 히트 영역 (캔버스 픽셀 기준, 매 렌더마다 갱신)
  type DrainDistLabel = { drainId: string; dir: 'top'|'bottom'|'left'|'right'; x: number; y: number; w: number; h: number };
  const drainDistLabelsRef = useRef<DrainDistLabel[]>([]);

  // 거리 입력 팝업
  const [drainDistEdit, setDrainDistEdit] = useState<{
    drainId: string;
    dir: 'top' | 'bottom' | 'left' | 'right';
    current: number;
    clientX: number;
    clientY: number;
  } | null>(null);

  const getBaseScale = useCallback(() => {
    const container = containerRef.current;
    if (!container || horizontal <= 0 || vertical <= 0) return 0.15;
    const availW = container.clientWidth - PADDING * 2 - 40;
    const availH = container.clientHeight - PADDING * 2 - 40;
    return Math.min(availW / horizontal, availH / vertical, 0.5);
  }, [horizontal, vertical]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.max(0.2, Math.min(5, prev + delta)));
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  const canvasToDrawing = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const cx = clientX - rect.left;
    const cy = clientY - rect.top;
    const { tx, ty, scale } = transformRef.current;
    const drawX = (cx - tx - PADDING) / scale;
    const drawY = (cy - ty - PADDING) / scale;
    return { x: Math.round(drawX), y: Math.round(drawY) };
  }, []);

  const isNearWall = useCallback((pt: {x: number, y: number}): { wallSide: 'left'|'right'|'top'|'bottom', position: number } | null => {
    if (pt.x < 0 || pt.x > horizontal || pt.y < 0 || pt.y > vertical) return null;
    const distTop = Math.abs(pt.y);
    const distBottom = Math.abs(pt.y - vertical);
    const distLeft = Math.abs(pt.x);
    const distRight = Math.abs(pt.x - horizontal);
    const minDist = Math.min(distTop, distBottom, distLeft, distRight);
    const threshold = panelThick * 2;
    if (minDist > threshold) return null;
    if (minDist === distTop) return { wallSide: 'top', position: pt.x };
    if (minDist === distBottom) return { wallSide: 'bottom', position: pt.x };
    if (minDist === distLeft) return { wallSide: 'left', position: pt.y };
    return { wallSide: 'right', position: pt.y };
  }, [horizontal, vertical, panelThick]);

  const findClickedPanel = useCallback((pt: {x: number, y: number}): { wallSide: 'left'|'right'|'top'|'bottom', index: number } | null => {
    if (poly) return null; // 다각형 엔진 패널 편집은 다음 단계에서 연결
    // 문 영역에 해당하는지 확인하는 헬퍼
    const isInDoorArea = (px: number, py: number, pw: number, ph: number) => {
      return doors.some(door => {
        const isHoriz = door.wallSide === 'top' || door.wallSide === 'bottom';
        const posOffset = (door.wallSide === 'top' || door.wallSide === 'right') ? panelThick : 0;
        let dx: number, dy: number, dw: number, dh: number;
        if (isHoriz) {
          dx = posOffset + door.position;
          dy = door.wallSide === 'top' ? 0 : vertical - panelThick;
          dw = door.leftPanel + door.width + door.rightPanel;
          dh = panelThick;
        } else {
          dx = door.wallSide === 'left' ? 0 : horizontal - panelThick;
          dy = posOffset + door.position;
          dw = panelThick;
          dh = door.leftPanel + door.width + door.rightPanel;
        }
        // 패널 영역과 문 영역이 겹치면 문 영역으로 판단
        return px < dx + dw && px + pw > dx && py < dy + dh && py + ph > dy;
      });
    };

    for (const side of ['left', 'top', 'right', 'bottom'] as const) {
      const panels = walls[side];
      const isVert = side === 'left' || side === 'right';
      const offset = (side === 'top' || side === 'right') ? panelThick : 0;
      for (let i = 0; i < panels.length; i++) {
        const p = panels[i];
        let px: number, py: number, pw: number, ph: number;
        if (isVert) {
          px = side === 'left' ? 0 : horizontal - panelThick;
          py = offset + p.y; pw = panelThick; ph = p.size;
        } else {
          px = offset + p.x;
          py = side === 'top' ? 0 : vertical - panelThick;
          pw = p.size; ph = panelThick;
        }
        if (pt.x >= px && pt.x <= px + pw && pt.y >= py && pt.y <= py + ph) {
          // 문 영역이면 패널 클릭 무시
          if (isInDoorArea(px, py, pw, ph)) continue;
          return { wallSide: side, index: i };
        }
      }
    }
    return null;
  }, [walls, doors, horizontal, vertical, panelThick, poly]);

  const findClickedDoor = useCallback((pt: {x: number, y: number}): string | null => {
    for (const door of doors) {
      const isHoriz = door.wallSide === 'top' || door.wallSide === 'bottom';
      const posOffset = (door.wallSide === 'top' || door.wallSide === 'right') ? panelThick : 0;
      let dx: number, dy: number, dw: number, dh: number;
      if (isHoriz) {
        dx = posOffset + door.position;
        dy = door.wallSide === 'top' ? 0 : vertical - panelThick;
        dw = door.leftPanel + door.width + door.rightPanel;
        dh = panelThick;
      } else {
        dx = door.wallSide === 'left' ? 0 : horizontal - panelThick;
        dy = posOffset + door.position;
        dw = panelThick;
        dh = door.leftPanel + door.width + door.rightPanel;
      }
      // 문 전체 영역(사이드패널 포함) + 약간의 여유
      const margin = 4;
      if (pt.x >= dx - margin && pt.x <= dx + dw + margin &&
          pt.y >= dy - margin && pt.y <= dy + dh + margin) {
        return door.id;
      }
    }
    return null;
  }, [doors, horizontal, vertical, panelThick]);

  const findClickedCeilingPanel = useCallback((pt: {x: number, y: number}): number | null => {
    for (const cp of ceilingPanels) {
      let px: number, py: number, pw: number, ph: number;
      if (ceilingDirection === 'H') {
        px = 0; py = cp.pos; pw = horizontal; ph = cp.size;
      } else {
        px = cp.pos; py = 0; pw = cp.size; ph = vertical;
      }
      if (pt.x >= px && pt.x <= px + pw && pt.y >= py && pt.y <= py + ph) {
        return cp.index;
      }
    }
    return null;
  }, [ceilingPanels, ceilingDirection, horizontal, vertical]);

  // 배수구 히트 영역 찾기
  const findClickedDrain = useCallback((pt: {x: number, y: number}): string | null => {
    for (const drain of drains) {
      const r = drain.size / 2 + 20;
      const dx = pt.x - drain.x;
      const dy = pt.y - drain.y;
      if (dx * dx + dy * dy <= r * r) return drain.id;
    }
    return null;
  }, [drains]);

  // 칸막이 히트 영역 찾기
  const findClickedMiddleWall = useCallback((pt: {x: number, y: number}): string | null => {
    for (const mw of middleWalls) {
      const margin = mw.panelThick;
      const mwCenter = panelThick + mw.position;
      if (mw.direction === 'vertical') {
        if (pt.x >= mwCenter - margin && pt.x <= mwCenter + margin &&
            pt.y >= panelThick && pt.y <= vertical - panelThick) return mw.id;
      } else {
        if (pt.y >= mwCenter - margin && pt.y <= mwCenter + margin &&
            pt.x >= panelThick && pt.x <= horizontal - panelThick) return mw.id;
      }
    }
    return null;
  }, [middleWalls, panelThick, horizontal, vertical]);

  const findClickedHari = useCallback((pt: { x: number; y: number }): string | null => {
    for (const hari of haris) {
      const rect = getHariRect(hari, horizontal, vertical, panelThick);
      if (!rect) continue;
      const margin = 6;
      if (pt.x >= rect.x - margin && pt.x <= rect.x + rect.w + margin &&
          pt.y >= rect.y - margin && pt.y <= rect.y + rect.h + margin) {
        return hari.id;
      }
    }
    return null;
  }, [haris, horizontal, vertical, panelThick]);

  const clickStartPos = useRef<{ x: number; y: number } | null>(null);
  // 문 끌기 (벽 방향으로만 이동)
  const doorDragStart = useRef<{ id: string; clientX: number; clientY: number; startPos: number; horiz: boolean; moved: boolean } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (activeTool === 'select') {
      // 배수구 드래그 체크
      const pt = canvasToDrawing(e.clientX, e.clientY);
      if (pt) {
        const drainId = findClickedDrain(pt);
        if (drainId) {
          const drain = drains.find(d => d.id === drainId);
          if (drain) {
            const dd = { id: drainId, x: drain.x, y: drain.y };
            setDraggingDrain(dd);
            draggingDrainRef.current = dd;
            drainDragStart.current = { clientX: e.clientX, clientY: e.clientY, drawX: drain.x, drawY: drain.y };
            e.stopPropagation();
            return;
          }
        }
        // 하리 클릭 체크
        const hariId = findClickedHari(pt);
        if (hariId) {
          onHariClick?.(hariId);
          e.stopPropagation();
          return;
        }
        // 문 끌기 체크 (끌지 않고 떼면 문 수정 창)
        if (onDoorDrag) {
          const doorId = findClickedDoor(pt);
          const door = doorId ? doors.find(d => d.id === doorId) : null;
          if (door) {
            doorDragStart.current = { id: door.id, clientX: e.clientX, clientY: e.clientY, startPos: door.position,
              horiz: door.wallSide === 'top' || door.wallSide === 'bottom', moved: false };
            e.stopPropagation();
            return;
          }
        }
        // 칸막이 드래그 체크
        const mwId = findClickedMiddleWall(pt);
        if (mwId) {
          const mw = middleWalls.find(w => w.id === mwId);
          if (mw) {
            draggingMWRef.current = { id: mw.id, position: mw.position };
            setDraggingMW({ id: mw.id, position: mw.position });
            mwDragStart.current = { clientX: e.clientX, clientY: e.clientY, startPos: mw.position, dir: mw.direction };
            e.stopPropagation();
            return;
          }
        }
      }
      clickStartPos.current = { x: e.clientX, y: e.clientY };
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY };
      offsetStart.current = { ...offset };
      return;
    }
    if (activeTool === 'delete') {
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY };
      offsetStart.current = { ...offset };
      return;
    }
    const pt = canvasToDrawing(e.clientX, e.clientY);
    if (!pt) return;
    if (activeTool === 'door') {
      const wall = isNearWall(pt);
      if (!wall) return;
      onWallClick?.(wall.wallSide, Math.max(0, wall.position));
    } else if (activeTool === 'drain') {
      if (pt.x > 0 && pt.x < horizontal && pt.y > 0 && pt.y < vertical) {
        onFloorClick?.(pt.x, pt.y);
      }
    } else if (activeTool === 'middleWall') {
      if (pt.x > panelThick && pt.x < horizontal - panelThick && pt.y > panelThick && pt.y < vertical - panelThick) {
        onFloorClick?.(pt.x, pt.y);
      }
    } else if (activeTool === 'hari') {
      if (pt.x >= 0 && pt.x <= horizontal && pt.y >= 0 && pt.y <= vertical) {
        onHariPlace?.(pt.x, pt.y);
      }
    } else if (activeTool === 'existing' && poly) {
      // 가장 가까운 외곽 변 (패널 두께 2배 이내) → 기존 벽 지정/해제
      const P = poly.outline, n = P.length;
      let best: { d: number; i: number } | null = null;
      for (let i = 0; i < n; i++) {
        const a = P[i], b = P[(i + 1) % n];
        const t = Math.max(0, Math.min(1, ((pt.x - a.x) * (b.x - a.x) + (pt.y - a.y) * (b.y - a.y)) / ((b.x - a.x) ** 2 + (b.y - a.y) ** 2)));
        const d = Math.hypot(pt.x - (a.x + t * (b.x - a.x)), pt.y - (a.y + t * (b.y - a.y)));
        if (!best || d < best.d) best = { d, i };
      }
      if (best && best.d < panelThick * 2.5) {
        const a = P[best.i], b = P[(best.i + 1) % n];
        const horiz = a.y === b.y;
        const key = `${Math.min(a.x, b.x)},${Math.min(a.y, b.y)}-${Math.max(a.x, b.x)},${Math.max(a.y, b.y)}`;
        onEdgeClick?.(key, horiz ? Math.min(a.x, b.x) : Math.min(a.y, b.y), horiz ? Math.max(a.x, b.x) : Math.max(a.y, b.y));
      }
    }
  }, [activeTool, offset, canvasToDrawing, isNearWall, horizontal, vertical, panelThick, onWallClick, onFloorClick, onHariPlace, drains, middleWalls, haris, findClickedDrain, findClickedMiddleWall, findClickedHari, poly, onEdgeClick, onDoorDrag, findClickedDoor, doors]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // 문 끌기 중
    const dd = doorDragStart.current;
    if (dd) {
      const d = dd.horiz ? e.clientX - dd.clientX : e.clientY - dd.clientY;
      if (!dd.moved && Math.abs(e.clientX - dd.clientX) < 4 && Math.abs(e.clientY - dd.clientY) < 4) return;
      dd.moved = true;
      const { scale } = transformRef.current;
      onDoorDrag?.(dd.id, dd.startPos + d / scale, 'move', 10 / scale); // 화면 10px 안이면 자석
      return;
    }
    // 배수구 드래그 중
    if (draggingDrainRef.current && drainDragStart.current) {
      const { scale } = transformRef.current;
      const dx = (e.clientX - drainDragStart.current.clientX) / scale;
      const dy = (e.clientY - drainDragStart.current.clientY) / scale;
      const newX = Math.round(Math.max(0, Math.min(horizontal, drainDragStart.current.drawX + dx)));
      const newY = Math.round(Math.max(0, Math.min(vertical, drainDragStart.current.drawY + dy)));
      const updated = { ...draggingDrainRef.current, x: newX, y: newY };
      setDraggingDrain(updated);
      draggingDrainRef.current = updated;
      return;
    }
    // 칸막이 드래그 중
    if (draggingMWRef.current && mwDragStart.current) {
      const { scale } = transformRef.current;
      const { dir, startPos } = mwDragStart.current;
      const delta = dir === 'vertical'
        ? (e.clientX - mwDragStart.current.clientX) / scale
        : (e.clientY - mwDragStart.current.clientY) / scale;
      const innerH = horizontal - panelThick * 2;
      const innerV = vertical - panelThick * 2;
      const maxPos = dir === 'vertical' ? innerH : innerV;
      const newPos = Math.round(Math.max(0, Math.min(maxPos, startPos + delta)));
      const updated = { ...draggingMWRef.current, position: newPos };
      setDraggingMW(updated);
      draggingMWRef.current = updated;
      return;
    }
    if (activeTool === 'door') {
      const pt = canvasToDrawing(e.clientX, e.clientY);
      if (pt) { setHoverWall(!!isNearWall(pt)); } else { setHoverWall(false); }
    } else if (activeTool === 'drain') {
      const pt = canvasToDrawing(e.clientX, e.clientY);
      if (pt && pt.x > panelThick && pt.x < horizontal - panelThick && pt.y > panelThick && pt.y < vertical - panelThick) {
        setHoverWall(true);
      } else { setHoverWall(false); }
    }
    if (!isPanning) return;
    setOffset({
      x: offsetStart.current.x + (e.clientX - panStart.current.x),
      y: offsetStart.current.y + (e.clientY - panStart.current.y),
    });
  }, [isPanning, activeTool, canvasToDrawing, isNearWall, horizontal, vertical, panelThick, onDoorDrag]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    // 문 끌기 종료 (안 움직였으면 문 수정 창)
    const dd = doorDragStart.current;
    if (dd) {
      doorDragStart.current = null;
      if (dd.moved) {
        const d = dd.horiz ? e.clientX - dd.clientX : e.clientY - dd.clientY;
        const { scale } = transformRef.current;
        onDoorDrag?.(dd.id, dd.startPos + d / scale, 'end', 10 / scale);
      } else onDoorClick?.(dd.id);
      return;
    }
    // 배수구 드래그 종료
    if (draggingDrainRef.current && drainDragStart.current) {
      const moved = Math.abs(e.clientX - drainDragStart.current.clientX) > 3 || Math.abs(e.clientY - drainDragStart.current.clientY) > 3;
      if (moved) {
        onDrainMove?.(draggingDrainRef.current.id, draggingDrainRef.current.x, draggingDrainRef.current.y);
      } else {
        // 클릭 → 거리 라벨 히트 체크 먼저
        const container = containerRef.current;
        const rect = container?.getBoundingClientRect();
        const px = rect ? e.clientX - rect.left : e.clientX;
        const py = rect ? e.clientY - rect.top : e.clientY;
        const labelHit = drainDistLabelsRef.current.find(
          lb => lb.drainId === draggingDrainRef.current!.id &&
            px >= lb.x && px <= lb.x + lb.w && py >= lb.y && py <= lb.y + lb.h
        );
        if (labelHit) {
          // 거리 라벨 클릭 → 해당 방향 거리 입력 팝업
          const drain = drains.find(d => d.id === labelHit.drainId);
          if (drain) {
            // 패널 안쪽 면 기준 거리
            const current = labelHit.dir === 'top'    ? drain.y - panelThick
              : labelHit.dir === 'bottom' ? (vertical - panelThick) - drain.y
              : labelHit.dir === 'left'   ? drain.x - panelThick
              : (horizontal - panelThick) - drain.x;
            setDrainDistEdit({ drainId: drain.id, dir: labelHit.dir, current: Math.round(current), clientX: e.clientX, clientY: e.clientY });
          }
        } else {
          // 배수구 본체 클릭 → 수정 팝업
          onDrainClick?.(draggingDrainRef.current.id);
        }
      }
      setDraggingDrain(null);
      draggingDrainRef.current = null;
      drainDragStart.current = null;
      return;
    }
    // 칸막이 드래그 종료
    if (draggingMWRef.current && mwDragStart.current) {
      const moved = Math.abs(e.clientX - mwDragStart.current.clientX) > 3 || Math.abs(e.clientY - mwDragStart.current.clientY) > 3;
      if (moved) {
        onMiddleWallMove?.(draggingMWRef.current.id, draggingMWRef.current.position);
      } else {
        // 누르기만 함 → 그 자리 칸막이 패널 편집 창 (칸막이 위치·방향은 그 창의 버튼으로)
        const pt = canvasToDrawing(e.clientX, e.clientY);
        const hit = pt && poly?.panels.find(p => p.pieceKey.startsWith(`part:${draggingMWRef.current!.id}#`) && (() => {
          const h = Math.abs(p.b.x - p.a.x) > Math.abs(p.b.y - p.a.y), t = panelThick / 2 + 2;
          const [x0, x1] = h ? [Math.min(p.a.x, p.b.x), Math.max(p.a.x, p.b.x)] : [p.a.x - t, p.a.x + t];
          const [y0, y1] = h ? [p.a.y - t, p.a.y + t] : [Math.min(p.a.y, p.b.y), Math.max(p.a.y, p.b.y)];
          return pt.x >= x0 && pt.x <= x1 && pt.y >= y0 && pt.y <= y1;
        })());
        if (hit && onPolyPanelClick) onPolyPanelClick(hit.id);
        else onMiddleWallClick?.(draggingMWRef.current.id);
      }
      setDraggingMW(null);
      draggingMWRef.current = null;
      mwDragStart.current = null;
      return;
    }
    setIsPanning(false);
    if (activeTool === 'select' && clickStartPos.current) {
      const dx = Math.abs(e.clientX - clickStartPos.current.x);
      const dy = Math.abs(e.clientY - clickStartPos.current.y);
      // 5px 이내 이동 = 클릭으로 판정
      if (dx < 5 && dy < 5) {
        const pt = canvasToDrawing(e.clientX, e.clientY);
        if (pt) {
          // 문을 항상 먼저 체크 (사이드패널 영역 포함)
          const doorHit = findClickedDoor(pt);
          if (doorHit) {
            onDoorClick?.(doorHit);
          } else {
            // 다각형 엔진 패널 클릭
            const polyHit = poly?.panels.find(p => {
              const h = Math.abs(p.b.x - p.a.x) > Math.abs(p.b.y - p.a.y);
              const [x0, x1] = h ? [Math.min(p.a.x, p.b.x), Math.max(p.a.x, p.b.x)] : [p.a.x - panelThick / 2, p.a.x + panelThick / 2];
              const [y0, y1] = h ? [p.a.y - panelThick / 2, p.a.y + panelThick / 2] : [Math.min(p.a.y, p.b.y), Math.max(p.a.y, p.b.y)];
              return pt.x >= x0 && pt.x <= x1 && pt.y >= y0 && pt.y <= y1;
            });
            if (polyHit) { onPolyPanelClick?.(polyHit.id); clickStartPos.current = null; return; }
            // 패널 클릭 시 해당 위치가 문 영역이 아닌 경우만 패널 선택
            const hit = findClickedPanel(pt);
            if (hit) {
              // 패널 위치가 문 영역과 겹치는지 재확인
              const doorOverlap = findClickedDoor(pt);
              if (!doorOverlap) onPanelClick?.(hit.wallSide, hit.index);
              else onDoorClick?.(doorOverlap);
            } else {
              const ceilingHit = findClickedCeilingPanel(pt);
              if (ceilingHit !== null) { onCeilingPanelClick?.(ceilingHit); }
            }
          }
        }
      }
      clickStartPos.current = null;
    }
  }, [activeTool, canvasToDrawing, findClickedPanel, findClickedCeilingPanel, findClickedDoor, onPanelClick, onCeilingPanelClick, onDoorClick, onDrainMove, onDrainClick, onMiddleWallMove, onMiddleWallClick, drains, horizontal, vertical, panelThick, poly, onPolyPanelClick, onDoorDrag]);

  // ── 터치 (패드·폰) ──
  // 한 손가락 = 마우스와 같음(이동·문 끌기·탭 선택), 두 손가락 = 그 사이를 중심으로 확대·축소, 두 번 탭 = 화면 맞춤
  // React 터치 이벤트는 passive라 기본 동작(페이지 확대·스크롤)을 못 막음 → 직접 등록
  const latest = useRef({ down: handleMouseDown, move: handleMouseMove, up: handleMouseUp, zoom, offset, horizontal, vertical, onDoorDrag });
  latest.current = { down: handleMouseDown, move: handleMouseMove, up: handleMouseUp, zoom, offset, horizontal, vertical, onDoorDrag };
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fake = (t: { clientX: number; clientY: number }) => ({ clientX: t.clientX, clientY: t.clientY, stopPropagation() {}, preventDefault() {} }) as unknown as React.MouseEvent;
    let single: { x0: number; y0: number; x: number; y: number } | null = null;
    let pinch: { d0: number; z0: number; base: number; wx: number; wy: number } | null = null;
    let lastTap = 0;
    // 손가락 하나로 하던 일(이동·끌기) 취소 — 두 번째 손가락이 닿으면 확대로 바뀜
    const cancelSingle = () => {
      single = null;
      clickStartPos.current = null;
      setIsPanning(false);
      const dd = doorDragStart.current;
      doorDragStart.current = null;
      if (dd?.moved) latest.current.onDoorDrag?.(dd.id, dd.startPos, 'end', 0); // 제자리로
      draggingDrainRef.current = null; drainDragStart.current = null; setDraggingDrain(null);
      draggingMWRef.current = null; mwDragStart.current = null; setDraggingMW(null);
    };
    const mid = (a: Touch, b: Touch) => {
      const r = el.getBoundingClientRect();
      return { x: (a.clientX + b.clientX) / 2 - r.left, y: (a.clientY + b.clientY) / 2 - r.top };
    };
    const start = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        const t = e.touches[0];
        single = { x0: t.clientX, y0: t.clientY, x: t.clientX, y: t.clientY };
        latest.current.down(fake(t));
      } else if (e.touches.length === 2) {
        cancelSingle();
        const [a, b] = [e.touches[0], e.touches[1]];
        const m = mid(a, b);
        const { tx, ty, scale } = transformRef.current;
        pinch = { d0: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1, z0: latest.current.zoom, base: scale / latest.current.zoom,
          wx: (m.x - tx - PADDING) / scale, wy: (m.y - ty - PADDING) / scale };
      }
    };
    const move = (e: TouchEvent) => {
      e.preventDefault();
      if (pinch && e.touches.length >= 2) {
        const [a, b] = [e.touches[0], e.touches[1]];
        const m = mid(a, b);
        const z = Math.max(0.2, Math.min(5, pinch.z0 * Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / pinch.d0));
        const sc = pinch.base * z, cw = el.clientWidth, ch = el.clientHeight;
        const { horizontal: H, vertical: V } = latest.current;
        // 손가락 사이 도면 점이 손가락 사이에 그대로 있게: tx = m.x − PADDING − wx·scale, tx = offset + cw/2 − H·scale/2 − PADDING
        setZoom(z);
        setOffset({ x: m.x - pinch.wx * sc - cw / 2 + (H * sc) / 2, y: m.y - pinch.wy * sc - ch / 2 + (V * sc) / 2 });
      } else if (single && e.touches.length === 1) {
        const t = e.touches[0];
        single.x = t.clientX; single.y = t.clientY;
        latest.current.move(fake(t));
      }
    };
    const end = (e: TouchEvent) => {
      e.preventDefault();
      if (pinch) { if (e.touches.length < 2) pinch = null; return; }
      if (!single || e.touches.length) return;
      const s0 = single;
      single = null;
      latest.current.up(fake({ clientX: s0.x, clientY: s0.y }));
      // 두 번 탭 → 화면 맞춤
      const tap = Math.hypot(s0.x - s0.x0, s0.y - s0.y0) < 10;
      const now = Date.now();
      if (tap && now - lastTap < 320) { setZoom(1); setOffset({ x: 0, y: 0 }); lastTap = 0; } else lastTap = tap ? now : 0;
    };
    el.addEventListener('touchstart', start, { passive: false });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end, { passive: false });
    el.addEventListener('touchcancel', end, { passive: false });
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, []);

  // 도면 그리기 — 화면과 인쇄 공용 (print: 흰 배경, 화면 맞춤, 선택·끌기 표시 없음)
  const renderScene = useCallback((canvas: HTMLCanvasElement, cw: number, ch: number, dpr: number, print = false) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = cw + 'px';
    canvas.style.height = ch + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = '#FFF';
    ctx.fillRect(0, 0, cw, ch);

    if (horizontal <= 0 || vertical <= 0) {
      ctx.fillStyle = '#999'; ctx.font = '16px Arial'; ctx.textAlign = 'center';
      ctx.fillText('가로/세로 값을 입력하세요', cw / 2, ch / 2);
      return;
    }

    const baseScale = print
      ? Math.min((cw - PADDING * 2 - 40) / horizontal, (ch - PADDING * 2 - 40) / vertical)
      : getBaseScale();
    const scale = print ? baseScale : baseScale * zoom;
    const offset = print ? { x: 0, y: 0 } : viewOffset;
    // 도면 원점(0,0)은 캔버스상에서 (tx+PADDING, ty+PADDING)에 위치
    // 도면 전체(치수선 포함)를 화면 중앙에 맞추기 위해 PADDING을 보정
    const tx = offset.x + cw / 2 - (horizontal * scale) / 2 - PADDING;
    const ty = offset.y + ch / 2 - (vertical * scale) / 2 - PADDING;
    if (!print) {
      transformRef.current = { tx, ty, scale };
      // 도면 mm 좌표 → 캔버스 안 화면 좌표 (매뉴얼 캡처 스크립트가 정확한 자리를 누를 때 씀)
      (window as any).__pdView = { ox: tx + PADDING, oy: ty + PADDING, scale };
    }

    ctx.save();
    ctx.translate(tx, ty);

    // 도면 배경
    ctx.fillStyle = '#FFF';
    ctx.fillRect(PADDING - 10, PADDING - 10, horizontal * scale + 20, vertical * scale + 20);

    // 벽 외곽선 (코너형 하리가 있는 모서리는 해당 구간 제외)
    if (poly) {
      drawExistingWalls(ctx, poly.outline, existingWalls, panelThick, { scale, ox: PADDING, oy: PADDING });
      drawOutline(ctx, poly.outline, { scale, ox: PADDING, oy: PADDING });
    }
    else {
      const cornerHaris = haris.filter(h => h.hariType === 'corner');
      const hariCorners = new Set(cornerHaris.map(h => h.corner));
      const getHW = (c: string) => {
        const h = cornerHaris.find(h => h.corner === c);
        return h ? { w: h.width * scale, hh: h.height * scale } : { w: 0, hh: 0 };
      };
      const tl = getHW('topLeft'), tr = getHW('topRight');
      const bl = getHW('bottomLeft'), br = getHW('bottomRight');
      const L = PADDING, T = PADDING;
      const R = PADDING + horizontal * scale, B = PADDING + vertical * scale;

      ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.beginPath();
      // 상단선: 좌→우 (topLeft 우측부터 ~ topRight 좌측까지)
      ctx.moveTo(L + tl.w, T); ctx.lineTo(R - tr.w, T);
      // 우상단 코너
      if (!hariCorners.has('topRight')) { ctx.moveTo(R, T); }
      else { ctx.moveTo(R - tr.w, T); ctx.lineTo(R - tr.w, T + tr.hh); }
      // 우측선
      ctx.moveTo(R, T + tr.hh); ctx.lineTo(R, B - br.hh);
      // 우하단 코너
      if (!hariCorners.has('bottomRight')) { ctx.moveTo(R, B); }
      else { ctx.moveTo(R, B - br.hh); ctx.lineTo(R - br.w, B - br.hh); }
      // 하단선
      ctx.moveTo(R - br.w, B); ctx.lineTo(L + bl.w, B);
      // 좌하단 코너
      if (!hariCorners.has('bottomLeft')) { ctx.moveTo(L, B); }
      else { ctx.moveTo(L + bl.w, B); ctx.lineTo(L + bl.w, B - bl.hh); }
      // 좌측선
      ctx.moveTo(L, B - bl.hh); ctx.lineTo(L, T + tl.hh);
      // 좌상단 코너
      if (!hariCorners.has('topLeft')) { ctx.moveTo(L, T); }
      else { ctx.moveTo(L, T + tl.hh); ctx.lineTo(L + tl.w, T + tl.hh); }
      ctx.stroke();
    }

    const PT = panelThick * scale; // 두께(화면 픽셀)

    // ─── PanelTest와 동일한 drawPanel 함수 ───
    // PL = 패널 길이(화면 픽셀), PT = 패널 두께(화면 픽셀)
    // 원본 패널 중심 = (PL/2, PT/2)
    // drawPanel(drawFn, screenCX, screenCY, angle):
    //   ctx.translate(screenCX, screenCY) → ctx.rotate(angle) → ctx.translate(-PL/2, -PT/2) → drawFn()

    const makeDraw = (
      PL: number, PT_: number, TD: number,
      lt: string, rt: string, sizeLabel: string,
    ) => () => {
      const PANEL_FILL = '#CCCCCC';
      // 본체
      ctx.fillStyle = PANEL_FILL; ctx.fillRect(0, 0, PL, PT_);
      ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.strokeRect(0, 0, PL, PT_);

      // 왼쪽 - 항상 그림 (데이터 정합으로 겹침 없음)
      if (lt === 'yangsu' || lt === 'corner') {
        ctx.beginPath();
        ctx.moveTo(0, 0); ctx.lineTo(-TD, PT_/2); ctx.lineTo(0, PT_);
        ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle = '#000'; ctx.stroke();
      } else if (lt === 'eumsu') {
        ctx.beginPath();
        ctx.moveTo(0, 0); ctx.lineTo(TD, PT_/2); ctx.lineTo(0, PT_);
        ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle = '#000'; ctx.stroke();
      }

      // 오른쪽
      if (rt === 'yangsu') {
        ctx.beginPath();
        ctx.moveTo(PL, 0); ctx.lineTo(PL + TD, PT_/2); ctx.lineTo(PL, PT_);
        ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle = '#000'; ctx.stroke();
      } else if (rt === 'eumsu') {
        ctx.beginPath();
        ctx.moveTo(PL, 0); ctx.lineTo(PL - TD, PT_/2); ctx.lineTo(PL, PT_);
        ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle = '#000'; ctx.stroke();
      } else if (rt === 'corner') {
        // 코너△: 밑변=하단선 PT만큼, 꼭짓점=내부 위쪽 (PanelTest 확정)
        ctx.beginPath();
        ctx.moveTo(PL - PT_, PT_); ctx.lineTo(PL - PT_/2, TD); ctx.lineTo(PL, PT_);
        ctx.closePath(); ctx.fillStyle = PANEL_FILL; ctx.fill(); ctx.strokeStyle = '#000'; ctx.stroke();
      }

      // 라벨
      const fontSize = Math.max(8, Math.min(FONT_SIZE, PT_ * 0.4));
      if (PT_ > 12 && PL > 20) {
        ctx.fillStyle = '#333'; ctx.font = `bold ${fontSize}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(sizeLabel, PL/2, PT_/2);
      }
    };

    const drawPanelAt = (
      drawFn: () => void,
      screenCX: number, screenCY: number,
      angle: number,
      PL: number, PT_: number
    ) => {
      ctx.save();
      ctx.translate(screenCX, screenCY);
      ctx.rotate(angle);
      ctx.translate(-PL/2, -PT_/2);
      drawFn();
      ctx.restore();
    };

    // 다각형 평면이면 천장은 실내(외곽선 안)로 잘라서 표시
    const clipToOutline = (dx: number, dy: number) => {
      if (!poly) return;
      ctx.beginPath();
      poly.outline.forEach((q, k) => { const x = dx + PADDING + q.x * scale, y = dy + PADDING + q.y * scale; if (k) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
      ctx.closePath(); ctx.clip();
    };
    // ── 천장 패널 렌더링 (벽 패널보다 먼저 그려서 벽이 위에 덮임) ──
    ctx.save(); clipToOutline(0, 0);
    if (ceilingPanels.length > 0) {
      const TD_C = PT * 0.5;
      ceilingPanels.forEach(cp => {
        let sx: number, sy: number, pw: number, ph: number;
        if (ceilingDirection === 'H') {
          sx = PADDING; sy = PADDING + cp.pos * scale;
          pw = cp.length * scale; ph = cp.size * scale;
        } else {
          sx = PADDING + cp.pos * scale; sy = PADDING;
          pw = cp.size * scale; ph = cp.length * scale;
        }
        ctx.strokeStyle = '#555'; ctx.lineWidth = 1;
        const isFirst = cp.index === 0;
        if (ceilingDirection === 'H') {
          ctx.beginPath();
          if (isFirst) { ctx.moveTo(sx, sy); ctx.lineTo(sx + pw, sy); }
          ctx.moveTo(sx, sy); ctx.lineTo(sx, sy + ph);
          ctx.moveTo(sx + pw, sy); ctx.lineTo(sx + pw, sy + ph);
          ctx.moveTo(sx, sy + ph); ctx.lineTo(sx + pw, sy + ph);
          ctx.stroke();
        } else {
          ctx.beginPath();
          if (isFirst) { ctx.moveTo(sx, sy); ctx.lineTo(sx, sy + ph); }
          ctx.moveTo(sx, sy); ctx.lineTo(sx + pw, sy);
          ctx.moveTo(sx, sy + ph); ctx.lineTo(sx + pw, sy + ph);
          ctx.moveTo(sx + pw, sy); ctx.lineTo(sx + pw, sy + ph);
          ctx.stroke();
        }
        ctx.strokeStyle = '#333'; ctx.lineWidth = 1.2;
        const drawCeilingTriangleAt = (bx: number, by: number, mid: number, isHoriz: boolean, type: string, isLeft: boolean) => {
          // mid: 삼각형 중심 위치 (H방향=x좌표, V방향=y좌표)
          const outDir = isLeft ? -1 : 1;
          ctx.beginPath();
          if (isHoriz) {
            if (type === 'yangsu') {
              ctx.moveTo(mid - TD_C, by); ctx.lineTo(mid, by + outDir * TD_C); ctx.lineTo(mid + TD_C, by);
            } else if (type === 'eumsu') {
              ctx.moveTo(mid - TD_C, by); ctx.lineTo(mid, by - outDir * TD_C); ctx.lineTo(mid + TD_C, by);
            }
          } else {
            if (type === 'yangsu') {
              ctx.moveTo(bx, mid - TD_C); ctx.lineTo(bx + outDir * TD_C, mid); ctx.lineTo(bx, mid + TD_C);
            } else if (type === 'eumsu') {
              ctx.moveTo(bx, mid - TD_C); ctx.lineTo(bx - outDir * TD_C, mid); ctx.lineTo(bx, mid + TD_C);
            }
          }
          ctx.closePath(); ctx.fillStyle = '#fff'; ctx.fill(); ctx.strokeStyle = '#333'; ctx.stroke();
        };
        const drawCeilingTriangle = (bx: number, by: number, isHoriz: boolean, type: string, isLeft: boolean) => {
          // 1/3, 2/3 두 지점에 표시
          if (isHoriz) {
            drawCeilingTriangleAt(bx, by, bx + pw / 3,     isHoriz, type, isLeft);
            drawCeilingTriangleAt(bx, by, bx + pw * 2 / 3, isHoriz, type, isLeft);
          } else {
            drawCeilingTriangleAt(bx, by, by + ph / 3,     isHoriz, type, isLeft);
            drawCeilingTriangleAt(bx, by, by + ph * 2 / 3, isHoriz, type, isLeft);
          }
        };

        if (ceilingDirection === 'H') {
          // leftType: 첫 패널만 그림 (이후는 이전 패널 rightType과 겹침)
          if (isFirst && cp.leftType !== 'none') {
            drawCeilingTriangle(sx, sy, true, cp.leftType, true);
          }
          // rightType: 항상 그림
          if (cp.rightType !== 'none') {
            drawCeilingTriangle(sx, sy + ph, true, cp.rightType, false);
          }
        } else {
          // leftType: 첫 패널만 그림
          if (isFirst && cp.leftType !== 'none') {
            drawCeilingTriangle(sx, sy, false, cp.leftType, true);
          }
          // rightType: 항상 그림
          if (cp.rightType !== 'none') {
            drawCeilingTriangle(sx + pw, sy, false, cp.rightType, false);
          }
        }
      });
    }
    ctx.restore(); // 천장 잘라내기 해제
    // 천장 패널 라벨 (벽 패널 이후에 그려야 보임 - 아래에서 별도 처리)

    // PanelTest 확정 규칙:
    // horizLen = horizontal - panelThick  (상/하단벽 실제 길이)
    // vertLen  = vertical  - panelThick   (좌/우측벽 실제 길이)
    // 좌측벽:  x=PADDING+PT/2,                    y=PADDING + posY*scale,          posY=0부터
    // 상단벽:  x=PADDING+PT + posX*scale,          y=PADDING+PT/2,                  posX=0부터
    // 우측벽:  x=PADDING+horizLen*scale+PT/2,      y=PADDING+PT + posY*scale,       posY=0부터
    // 하단벽:  x=PADDING + posX*scale,             y=PADDING+vertLen*scale+PT/2,    posX=0부터
    if (poly) {
      // 다각형 엔진: 벽·하리 패널 (선명 렌더, 라벨 바로 세움)
      drawPolyPanels(ctx, poly.panels, panelThick, { scale, ox: PADDING, oy: PADDING }, { highlight: print ? null : selectedPolyId, labelOf: panelNos ? (p => panelNos[p.id]) : undefined });
    } else {
    // ── 좌측벽 ── (panel.y = 실제 위치)
    walls.left.forEach((panel) => {
      const PL_ = panel.size * scale;
      const TD = PT * 0.5;
      const screenCX = PADDING + PT / 2;
      const screenCY = PADDING + panel.y * scale + PL_ / 2;
      const drawFn = makeDraw(PL_, PT, TD, panel.leftType || 'yangsu', panel.rightType || 'eumsu', `${panel.size}`);
      drawPanelAt(drawFn, screenCX, screenCY, -Math.PI / 2, PL_, PT);
    });

    // ── 상단벽 ── (panel.x = panelThick부터의 실제 위치)
    walls.top.forEach((panel) => {
      const PL_ = panel.size * scale;
      const TD = PT * 0.5;
      const screenCX = PADDING + (panelThick + panel.x) * scale + PL_ / 2;
      const screenCY = PADDING + PT / 2;
      const drawFn = makeDraw(PL_, PT, TD, panel.leftType || 'yangsu', panel.rightType || 'eumsu', `${panel.size}`);
      drawPanelAt(drawFn, screenCX, screenCY, 0, PL_, PT);
    });

    // ── 우측벽 ── (panel.y = panelThick부터의 실제 위치)
    walls.right.forEach((panel) => {
      const PL_ = panel.size * scale;
      const TD = PT * 0.5;
      const screenCX = PADDING + (horizontal - panelThick) * scale + PT / 2;
      const screenCY = PADDING + (panelThick + panel.y) * scale + PL_ / 2;
      const drawFn = makeDraw(PL_, PT, TD, panel.leftType || 'yangsu', panel.rightType || 'eumsu', `${panel.size}`);
      drawPanelAt(drawFn, screenCX, screenCY, -Math.PI * 1.5, PL_, PT);
    });

    // ── 하단벽 ── (panel.x = 실제 위치, 0부터 시작)
    walls.bottom.forEach((panel) => {
      const PL_ = panel.size * scale;
      const TD = PT * 0.5;
      const screenCX = PADDING + panel.x * scale + PL_ / 2;
      const screenCY = PADDING + (vertical - panelThick) * scale + PT / 2;
      const drawFn = makeDraw(PL_, PT, TD, panel.leftType || 'yangsu', panel.rightType || 'eumsu', `${panel.size}`);
      drawPanelAt(drawFn, screenCX, screenCY, -Math.PI, PL_, PT);
    });
    }

    // 문 렌더링 (DoorTest 확정 방식: 로컬좌표 + 회전)
    const WALL_ANGLES: Record<string, number> = {
      top: 0, bottom: Math.PI, left: -Math.PI / 2, right: -Math.PI * 1.5,
    };

    doors.forEach(door => {
      const posOffset = (door.wallSide === 'top' || door.wallSide === 'right') ? panelThick : 0;
      const isHoriz = door.wallSide === 'top' || door.wallSide === 'bottom';

      // 문 전체(사이드패널+문본체)의 화면상 중심점 계산
      const totalLen = door.leftPanel + door.width + door.rightPanel; // mm
      const totalPx = totalLen * scale; // 픽셀
      // 문 시작점 (화면 픽셀)
      let startSX: number, startSY: number;
      if (isHoriz) {
        startSX = PADDING + (posOffset + door.position) * scale;
        startSY = PADDING + (door.wallSide === 'top' ? 0 : (vertical - panelThick)) * scale;
      } else {
        startSX = PADDING + (door.wallSide === 'left' ? 0 : (horizontal - panelThick)) * scale;
        startSY = PADDING + (posOffset + door.position) * scale;
      }

      // 회전 중심: 문 전체의 중앙
      const angle = WALL_ANGLES[door.wallSide];
      const cx = isHoriz ? startSX + totalPx / 2 : startSX + PT / 2;
      const cy = isHoriz ? startSY + PT / 2        : startSY + totalPx / 2;

      // 삼각형 (로컬 좌표)
      const TD_D = PT * 0.5;
      const drawTriLocal = (x: number, type: 'yangsu' | 'eumsu', side: 'left' | 'right') => {
        ctx.beginPath();
        if (side === 'left') {
          if (type === 'yangsu') { ctx.moveTo(x, 0); ctx.lineTo(x - TD_D, PT/2); ctx.lineTo(x, PT); }
          else                   { ctx.moveTo(x, 0); ctx.lineTo(x + TD_D, PT/2); ctx.lineTo(x, PT); }
        } else {
          if (type === 'yangsu') { ctx.moveTo(x, 0); ctx.lineTo(x + TD_D, PT/2); ctx.lineTo(x, PT); }
          else                   { ctx.moveTo(x, 0); ctx.lineTo(x - TD_D, PT/2); ctx.lineTo(x, PT); }
        }
        ctx.closePath();
        ctx.fillStyle = '#FFF'; ctx.fill(); ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.stroke();
      };

      // 문짝 + 호 (로컬 좌표)
      const drawLeafLocal = (hx: number, hy: number, toRight: boolean, leafLen: number) => {
        const ex = hx + (toRight ? leafLen : -leafLen);
        const outerDir = -1; // 로컬좌표에서 항상 위(-1), 회전으로 실제 방향 결정
        ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(ex, hy); ctx.stroke();

        const startA = toRight ? 0 : Math.PI;
        const endA   = -Math.PI / 2; // outerDir=-1이므로 항상 위
        const ccw    = toRight ? true : false;
        ctx.save(); ctx.translate(hx, hy); ctx.scale(1, 0.25);
        ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(0, 0, leafLen, startA, endA, ccw); ctx.stroke();
        ctx.restore();

        const arcEndY = hy + outerDir * leafLen * 0.25;
        ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx, arcEndY); ctx.stroke();
      };

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      // 로컬 좌표: 문 전체 왼쪽 끝 = (-totalPx/2, 0), 패널 위쪽 = y=0
      ctx.translate(-totalPx / 2, -PT / 2);

      const lpPx = door.leftPanel * scale;
      const dwPx = door.width * scale;
      const rpPx = door.rightPanel * scale;
      const doorX = lpPx; // 문 본체 시작 X (로컬)

      // panelVariant: 1=양수/음수, 2=음수/양수, 3=음수/음수, 4=양수/양수 (로컬 좌표 기준)
      // panelVariant: 1=양수/음수, 2=음수/양수, 3=음수/음수, 4=양수/양수
      // 1=양수/음수, 2=음수/양수, 3=음수/음수, 4=양수/양수
      const pv = door.panelVariant ?? 1;
      const pvLt: 'yangsu'|'eumsu' = (pv === 1 || pv === 4) ? 'yangsu' : 'eumsu';
      const pvRt: 'yangsu'|'eumsu' = (pv === 2 || pv === 4) ? 'yangsu' : 'eumsu';
      // bottom/right는 반시계 기준 반전
      const isReversed = door.wallSide === 'bottom' || door.wallSide === 'right';
      // 새 엔진: 엔진이 정한 화면 기준 끝(lo = 왼/위)을 로컬 좌우로 — 로컬 왼쪽이 화면 lo인 벽은 상단·우측
      const de = doorEnds?.[door.id];
      const localLeftIsLo = door.wallSide === 'top' || door.wallSide === 'right';
      const leftEndType  = de ? (localLeftIsLo ? de.lo : de.hi) : (isReversed ? pvRt : pvLt);
      const rightEndType = de ? (localLeftIsLo ? de.hi : de.lo) : (isReversed ? pvLt : pvRt);
      // 좌측 사이드 패널
      if (door.leftPanel > 0) {
        ctx.fillStyle = '#FFE0B2'; ctx.fillRect(0, 0, lpPx, PT);
        ctx.strokeStyle = '#F57C00'; ctx.lineWidth = 1.5; ctx.strokeRect(0, 0, lpPx, PT);
        drawTriLocal(0, leftEndType, 'left');
      }

      // 문 본체
      ctx.fillStyle = '#C8E6C9'; ctx.fillRect(doorX, 0, dwPx, PT);
      ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 1.5; ctx.strokeRect(doorX, 0, dwPx, PT);
      const fontSize = Math.max(8, Math.min(FONT_SIZE, PT * 0.4));
      ctx.fillStyle = '#2E7D32'; ctx.font = `bold ${fontSize}px "Malgun Gothic", Arial, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      // 라벨은 벽 회전과 무관하게 바로 읽히게 (가로: 왼→오, 세로: 아래→위)
      ctx.save();
      ctx.translate(doorX + dwPx / 2, PT / 2);
      ctx.rotate(-angle + (isHoriz ? 0 : -Math.PI / 2));
      ctx.fillText(`${door.width}`, 0, 0);
      ctx.restore();

      // 우측 사이드 패널
      if (door.rightPanel > 0) {
        ctx.fillStyle = '#FFE0B2'; ctx.fillRect(doorX + dwPx, 0, rpPx, PT);
        ctx.strokeStyle = '#F57C00'; ctx.lineWidth = 1.5; ctx.strokeRect(doorX + dwPx, 0, rpPx, PT);
        drawTriLocal(doorX + dwPx + rpPx, rightEndType, 'right');
      }

      // 문짝 호 (로컬 좌표, 패널 위쪽 y=0이 바깥)
      const doorType = door.doorType || 'single-right';
      if (doorType === 'single-right') {
        drawLeafLocal(doorX, 0, true, dwPx);
      } else if (doorType === 'single-left') {
        drawLeafLocal(doorX + dwPx, 0, false, dwPx);
      } else {
        drawLeafLocal(doorX, 0, true, dwPx / 2);
        drawLeafLocal(doorX + dwPx, 0, false, dwPx / 2);
      }

      ctx.restore();

      // 문 전체 치수선 (사이드패널+문본체 합계)
      const totalLabel = `${door.leftPanel + door.width + door.rightPanel}`;
      ctx.strokeStyle = '#666'; ctx.lineWidth = 0.5;
      ctx.fillStyle = '#333'; ctx.font = `${Math.max(8, FONT_SIZE - 1)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const dimOff = DIM_OFFSET;
      if (isHoriz) {
        const x1 = startSX, x2 = startSX + totalPx;
        const dimY = door.wallSide === 'top' ? startSY - dimOff : startSY + PT + dimOff;
        const tickDir = door.wallSide === 'top' ? 1 : -1;
        ctx.beginPath();
        ctx.moveTo(x1, startSY + tickDir * 5); ctx.lineTo(x1, dimY + tickDir * 5);
        ctx.moveTo(x2, startSY + tickDir * 5); ctx.lineTo(x2, dimY + tickDir * 5);
        ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x1, dimY); ctx.lineTo(x2, dimY); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x1, dimY - 4); ctx.lineTo(x1, dimY + 4);
        ctx.moveTo(x2, dimY - 4); ctx.lineTo(x2, dimY + 4);
        ctx.stroke();
        ctx.fillText(totalLabel, (x1 + x2) / 2, dimY + tickDir * 10);
      } else {
        const y1 = startSY, y2 = startSY + totalPx;
        const dimX = door.wallSide === 'left' ? startSX - dimOff : startSX + PT + dimOff;
        const tickDir = door.wallSide === 'left' ? 1 : -1;
        ctx.beginPath();
        ctx.moveTo(startSX + tickDir * 5, y1); ctx.lineTo(dimX + tickDir * 5, y1);
        ctx.moveTo(startSX + tickDir * 5, y2); ctx.lineTo(dimX + tickDir * 5, y2);
        ctx.stroke();
        ctx.beginPath(); ctx.moveTo(dimX, y1); ctx.lineTo(dimX, y2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(dimX - 4, y1); ctx.lineTo(dimX + 4, y1);
        ctx.moveTo(dimX - 4, y2); ctx.lineTo(dimX + 4, y2);
        ctx.stroke();
        ctx.save(); ctx.translate(dimX + tickDir * 10, (y1 + y2) / 2);
        ctx.rotate(-Math.PI / 2); ctx.fillText(totalLabel, 0, 0); ctx.restore();
      }
    });

    // 문 끌기 안내: 실내 쪽에 양옆 남는 거리 + 자석 맞춤
    if (doorGuide && !print) {
      const g = doorGuide, horiz = g.side === 'top' || g.side === 'bottom';
      const inward = g.side === 'top' || g.side === 'left' ? 1 : -1;
      const base = g.side === 'top' || g.side === 'left' ? panelThick : (horiz ? vertical : horizontal) - panelThick;
      const c = (horiz ? PADDING : PADDING) + base * scale + inward * 26; // 안내선 위치 (화면)
      const P = (along: number, across: number) => (horiz ? [PADDING + along * scale, across] : [across, PADDING + along * scale]) as [number, number];
      ctx.save();
      ctx.strokeStyle = '#1565C0'; ctx.fillStyle = '#1565C0'; ctx.lineWidth = 1.2;
      ctx.font = `bold ${FONT_SIZE + 1}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const seg = (a: number, b: number) => {
        if (b - a <= 0) return;
        const [x1, y1] = P(a, c), [x2, y2] = P(b, c);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        [[x1, y1], [x2, y2]].forEach(([x, y]) => {
          ctx.beginPath();
          if (horiz) { ctx.moveTo(x, y - 6); ctx.lineTo(x, y + 6); } else { ctx.moveTo(x - 6, y); ctx.lineTo(x + 6, y); }
          ctx.stroke();
        });
        const [mx, my] = P((a + b) / 2, c + inward * 12);
        const label = `${Math.round(b - a)}`;
        const w = ctx.measureText(label).width + 8;
        ctx.save(); ctx.fillStyle = 'rgba(255,255,255,0.9)';
        if (horiz) ctx.fillRect(mx - w / 2, my - 8, w, 16); else ctx.fillRect(mx - 8, my - w / 2, 16, w);
        ctx.restore();
        ctx.save(); ctx.translate(mx, my); if (!horiz) ctx.rotate(-Math.PI / 2); ctx.fillText(label, 0, 0); ctx.restore();
      };
      seg(g.slot.lo, g.from);
      seg(g.to, g.slot.hi);
      if (g.snap) {
        const [sx, sy] = P((g.from + g.to) / 2, c + inward * 30);
        const text = `🧲 ${g.snap}`, w = ctx.measureText(text).width + 12;
        ctx.save(); ctx.translate(sx, sy); if (!horiz) ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#E8F5E9'; ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 1;
        ctx.fillRect(-w / 2, -10, w, 20); ctx.strokeRect(-w / 2, -10, w, 20);
        ctx.fillStyle = '#2E7D32'; ctx.fillText(text, 0, 0); ctx.restore();
      }
      ctx.restore();
    }

    // 치수선
    const drawArrow = (x1: number, y1: number, x2: number, y2: number) => {
      const headLen = 5; ctx.beginPath();
      if (y1 === y2) {
        ctx.moveTo(x1, y1 - headLen); ctx.lineTo(x1, y1 + headLen);
        ctx.moveTo(x2, y2 - headLen); ctx.lineTo(x2, y2 + headLen);
      } else {
        ctx.moveTo(x1 - headLen, y1); ctx.lineTo(x1 + headLen, y1);
        ctx.moveTo(x2 - headLen, y2); ctx.lineTo(x2 + headLen, y2);
      }
      ctx.stroke();
    };

    const drawDim = (x1: number, y1: number, x2: number, y2: number, label: string, side: string, off: number) => {
      const sx1 = PADDING + x1 * scale, sy1 = PADDING + y1 * scale;
      const sx2 = PADDING + x2 * scale, sy2 = PADDING + y2 * scale;
      const len = Math.abs(side === 'top' || side === 'bottom' ? sx2 - sx1 : sy2 - sy1);
      if (len < 20) return;
      ctx.strokeStyle = '#666'; ctx.lineWidth = 0.5;
      ctx.fillStyle = '#333'; ctx.font = `${FONT_SIZE}px "Malgun Gothic", Arial, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (side === 'top') {
        const dy = sy1 - off;
        ctx.beginPath(); ctx.moveTo(sx1, sy1 - 5); ctx.lineTo(sx1, dy - 5);
        ctx.moveTo(sx2, sy2 - 5); ctx.lineTo(sx2, dy - 5); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx1, dy); ctx.lineTo(sx2, dy); ctx.stroke();
        drawArrow(sx1, dy, sx2, dy); ctx.fillText(label, (sx1+sx2)/2, dy - 8);
      } else if (side === 'bottom') {
        const dy = sy1 + off;
        ctx.beginPath(); ctx.moveTo(sx1, sy1 + 5); ctx.lineTo(sx1, dy + 5);
        ctx.moveTo(sx2, sy2 + 5); ctx.lineTo(sx2, dy + 5); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sx1, dy); ctx.lineTo(sx2, dy); ctx.stroke();
        drawArrow(sx1, dy, sx2, dy); ctx.fillText(label, (sx1+sx2)/2, dy + 12);
      } else if (side === 'left') {
        const dx = sx1 - off;
        ctx.beginPath(); ctx.moveTo(sx1 - 5, sy1); ctx.lineTo(dx - 5, sy1);
        ctx.moveTo(sx2 - 5, sy2); ctx.lineTo(dx - 5, sy2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(dx, sy1); ctx.lineTo(dx, sy2); ctx.stroke();
        drawArrow(dx, sy1, dx, sy2);
        ctx.save(); ctx.translate(dx - 8, (sy1+sy2)/2); ctx.rotate(-Math.PI/2); ctx.fillText(label, 0, 0); ctx.restore();
      } else if (side === 'right') {
        const dx = sx1 + off;
        ctx.beginPath(); ctx.moveTo(sx1 + 5, sy1); ctx.lineTo(dx + 5, sy1);
        ctx.moveTo(sx2 + 5, sy2); ctx.lineTo(dx + 5, sy2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(dx, sy1); ctx.lineTo(dx, sy2); ctx.stroke();
        drawArrow(dx, sy1, dx, sy2);
        ctx.save(); ctx.translate(dx + 12, (sy1+sy2)/2); ctx.rotate(-Math.PI/2); ctx.fillText(label, 0, 0); ctx.restore();
      }
    };

    // 치수선 - panel.x/y 기준 (문 영역 공백 자동 반영)
    if (poly) {
      // 외벽 띠에 있는 패널만 면별 치수 (하리 안쪽 패널은 생략)
      poly.panels.forEach(p => {
        const x0 = Math.min(p.a.x, p.b.x), x1 = Math.max(p.a.x, p.b.x), y0 = Math.min(p.a.y, p.b.y), y1 = Math.max(p.a.y, p.b.y);
        const horiz = x1 - x0 > y1 - y0;
        if (horiz && p.a.y < panelThick) drawDim(x0, 0, x1, 0, `${p.size}`, 'top', DIM_OFFSET);
        else if (horiz && p.a.y > vertical - panelThick) drawDim(x0, vertical, x1, vertical, `${p.size}`, 'bottom', DIM_OFFSET);
        else if (!horiz && p.a.x < panelThick) drawDim(0, y0, 0, y1, `${p.size}`, 'left', DIM_OFFSET);
        else if (!horiz && p.a.x > horizontal - panelThick) drawDim(horizontal, y0, horizontal, y1, `${p.size}`, 'right', DIM_OFFSET);
      });
    } else {
      walls.top.forEach(p => { drawDim(panelThick + p.x, 0, panelThick + p.x + p.size, 0, `${p.size}`, 'top', DIM_OFFSET); });
      walls.bottom.forEach(p => { drawDim(p.x, vertical, p.x + p.size, vertical, `${p.size}`, 'bottom', DIM_OFFSET); });
      walls.left.forEach(p => { drawDim(0, p.y, 0, p.y + p.size, `${p.size}`, 'left', DIM_OFFSET); });
      walls.right.forEach(p => { drawDim(horizontal, panelThick + p.y, horizontal, panelThick + p.y + p.size, `${p.size}`, 'right', DIM_OFFSET); });
    }
    // 안쪽 변 치수 (잘라낸 모양·하리 홈의 변 = 바깥 상자 둘레에 없는 변): 변 길이를 건물 바깥(빈 쪽)에
    if (poly && poly.outline.length > 4) {
      const pts = poly.outline, n = pts.length;
      const insideAt = (x: number, y: number) => {
        let c = false;
        for (let i = 0, j = n - 1; i < n; j = i++) {
          const a = pts[i], b = pts[j];
          if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) c = !c;
        }
        return c;
      };
      const drawn: string[] = [];
      pts.forEach((a, i) => {
        const b = pts[(i + 1) % n];
        const horiz = a.y === b.y;
        if (horiz ? a.y === 0 || a.y === vertical : a.x === 0 || a.x === horizontal) return; // 바깥 둘레 변은 위 치수선에 있음
        const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x), y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
        const L = Math.round(horiz ? x1 - x0 : y1 - y0);
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, e = panelThick / 2;
        const side = horiz ? (insideAt(mx, my - e) ? 'bottom' : 'top') : (insideAt(mx - e, my) ? 'right' : 'left');
        // 변에서 바깥쪽으로 상자 둘레까지 막힘 없이 트였나 (다른 평행 변이 사이에 없음)
        const open = !pts.some((p, k) => {
          const q = pts[(k + 1) % n];
          if (k === i || (horiz ? p.y !== q.y : p.x !== q.x)) return false;
          const c = horiz ? p.y : p.x, lo = horiz ? Math.min(p.x, q.x) : Math.min(p.y, q.y), hi = horiz ? Math.max(p.x, q.x) : Math.max(p.y, q.y);
          const e0 = horiz ? x0 : y0, e1 = horiz ? x1 : y1, at = horiz ? a.y : a.x;
          if (Math.min(hi, e1) - Math.max(lo, e0) <= 0) return false;
          return side === 'top' || side === 'left' ? c < at && c >= 0 && c !== at : c > at;
        });
        if (open) {
          // 트인 변 → 그 쪽 바깥 치수줄(패널 치수와 같은 줄)에 홈 폭으로
          if (side === 'top') drawDim(x0, 0, x1, 0, `${L}`, 'top', DIM_OFFSET);
          else if (side === 'bottom') drawDim(x0, vertical, x1, vertical, `${L}`, 'bottom', DIM_OFFSET);
          else if (side === 'left') drawDim(0, y0, 0, y1, `${L}`, 'left', DIM_OFFSET);
          else drawDim(horizontal, y0, horizontal, y1, `${L}`, 'right', DIM_OFFSET);
          return;
        }
        // 막힌 변(홈 옆면) → 홈 안에, 마주 보는 같은 구간 변은 한 번만
        const span = horiz ? `h${x0}-${x1}` : `v${y0}-${y1}`;
        if (drawn.includes(span)) return;
        drawn.push(span);
        drawDim(x0, y0, x1, y1, `${L}`, side, 26);
      });
    }

    // 전체 치수선
    drawDim(0, 0, horizontal, 0, `${horizontal}`, 'top', DIM_OFFSET2);
    drawDim(0, 0, 0, vertical, `${vertical}`, 'left', DIM_OFFSET2);
    drawDim(0, vertical, horizontal, vertical, `${horizontal}`, 'bottom', DIM_OFFSET2);
    drawDim(horizontal, 0, horizontal, vertical, `${vertical}`, 'right', DIM_OFFSET2);

    ctx.restore();

    // 배수구 렌더링 (restore 이후 — tx/ty 직접 더함)
    const newLabels: DrainDistLabel[] = [];
    const otherBoxes: { x: number; y: number; w: number; h: number }[] = []; // 칸막이 위치 글자 등 (천장 글자가 피함)
    drains.forEach(drain => {
      const dd = print ? null : draggingDrain;
      const drainX = (dd && dd.id === drain.id) ? dd.x : drain.x;
      const drainY = (dd && dd.id === drain.id) ? dd.y : drain.y;
      const isDragging = !!(dd && dd.id === drain.id);

      const cx = tx + PADDING + drainX * scale;
      const cy = ty + PADDING + drainY * scale;
      const r  = (drain.size / 2) * scale;

      // 거리선: 패널 안쪽 면 기준
      const wallTop    = ty + PADDING + panelThick * scale;
      const wallBottom = ty + PADDING + (vertical - panelThick) * scale;
      const wallLeft   = tx + PADDING + panelThick * scale;
      const wallRight  = tx + PADDING + (horizontal - panelThick) * scale;

      const distTop    = Math.round(drainY - panelThick);
      const distBottom = Math.round((vertical - panelThick) - drainY);
      const distLeft   = Math.round(drainX - panelThick);
      const distRight  = Math.round((horizontal - panelThick) - drainX);

      const AH = 5;
      const drawDistLine = (
        x1: number, y1: number, x2: number, y2: number,
        label: string, isHoriz: boolean,
        dir: 'top' | 'bottom' | 'left' | 'right'
      ) => {
        const lx = (x1 + x2) / 2;
        const ly = (y1 + y2) / 2;
        ctx.strokeStyle = isDragging ? '#0D47A1' : '#555';
        ctx.lineWidth = isDragging ? 1.2 : 0.8;
        ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        ctx.setLineDash([]);
        ctx.lineWidth = isDragging ? 1.2 : 0.8;
        ctx.beginPath();
        if (isHoriz) {
          ctx.moveTo(x1 + AH, y1 - AH); ctx.lineTo(x1, y1); ctx.lineTo(x1 + AH, y1 + AH);
          ctx.moveTo(x2 - AH, y2 - AH); ctx.lineTo(x2, y2); ctx.lineTo(x2 - AH, y2 + AH);
        } else {
          ctx.moveTo(x1 - AH, y1 + AH); ctx.lineTo(x1, y1); ctx.lineTo(x1 + AH, y1 + AH);
          ctx.moveTo(x2 - AH, y2 - AH); ctx.lineTo(x2, y2); ctx.lineTo(x2 + AH, y2 - AH);
        }
        ctx.stroke();
        ctx.font = 'bold 10px Arial';
        const tw = ctx.measureText(label).width;
        const bw = tw + 10, bh = 16;
        // 라벨 배경 (클릭 가능 강조)
        ctx.fillStyle = isDragging ? '#1565C0' : '#E3F2FD';
        ctx.fillRect(lx - bw / 2, ly - bh / 2, bw, bh);
        ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 1; ctx.setLineDash([]);
        ctx.strokeRect(lx - bw / 2, ly - bh / 2, bw, bh);
        ctx.fillStyle = isDragging ? '#fff' : '#1565C0';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(label, lx, ly);
        // 히트 영역 저장
        newLabels.push({ drainId: drain.id, dir, x: lx - bw / 2, y: ly - bh / 2, w: bw, h: bh });
      };

      // 가까운 쪽만 표시
      if (distTop <= distBottom) {
        drawDistLine(cx, wallTop, cx, cy, `${distTop}`, false, 'top');
      } else {
        drawDistLine(cx, cy, cx, wallBottom, `${distBottom}`, false, 'bottom');
      }
      if (distLeft <= distRight) {
        drawDistLine(wallLeft, cy, cx, cy, `${distLeft}`, true, 'left');
      } else {
        drawDistLine(cx, cy, wallRight, cy, `${distRight}`, true, 'right');
      }

      // 배수구 원
      ctx.strokeStyle = isDragging ? '#0D47A1' : '#1565C0'; ctx.lineWidth = isDragging ? 2.5 : 2;
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.6, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(cx - r, cy); ctx.lineTo(cx + r, cy);
      ctx.moveTo(cx, cy - r); ctx.lineTo(cx, cy + r); ctx.stroke();

      // 방향 화살표 (원 안, 굵은 화살표)
      // 방향별 각도: up=0, right=90, down=180, left=270
      const dirAngle: Record<string, number> = { up: 0, right: Math.PI / 2, down: Math.PI, left: -Math.PI / 2 };
      const angle = dirAngle[drain.direction] ?? 0;
      // 방향 화살표: 원 바깥, 원 끝에서 시작해 위쪽(방향)으로 뻗음
      const arrowBodyLen = r * 1.2;  // 몸체 길이
      const headLen2 = r * 0.45;     // 삼각형 머리 높이
      const headW2   = r * 0.32;     // 삼각형 반너비
      const stemW2   = r * 0.12;     // 몸체 반너비
      const col = isDragging ? '#B71C1C' : '#E53935';

      // 원 끝에서 시작: y=−r (위방향 기준), 위로 arrowBodyLen+headLen2 만큼
      const tailY2 = -r;                          // 원 끝 (꼬리)
      const tipY2  = tailY2 - arrowBodyLen - headLen2; // 화살 꼭짓점
      const headBot2 = tailY2 - arrowBodyLen;     // 삼각형 밑변

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      ctx.fillStyle = col;

      // 몸체
      ctx.fillRect(-stemW2, headBot2, stemW2 * 2, tailY2 - headBot2);
      // 삼각형 머리
      ctx.beginPath();
      ctx.moveTo(0, tipY2);
      ctx.lineTo(-headW2, headBot2);
      ctx.lineTo(headW2, headBot2);
      ctx.closePath();
      ctx.fill();

      ctx.restore();

    });
    if (!print) drainDistLabelsRef.current = newLabels;

    // 하리 렌더링 (restore 이후 — tx/ty 직접 더함)
    haris.forEach(hari => {
      const rect = getHariRect(hari, horizontal, vertical, panelThick);
      if (!rect) return;

      const sx = tx + PADDING + rect.x * scale;
      const sy = ty + PADDING + rect.y * scale;
      const sw = rect.w * scale;
      const sh = rect.h * scale;
      const PT_PX = panelThick * scale;

      if (hari.hariType === 'corner' && !poly) {
        const c = hari.corner!;
        // 하리 안쪽 꺾임에 패널 두께만큼 회색 연결 패널 그리기
        // topLeft: 수평 연결(상단벽 높이) + 수직 연결(좌측벽 너비)
        ctx.fillStyle = '#CCCCCC';
        ctx.strokeStyle = '#000'; ctx.lineWidth = 2;

        if (c === 'topLeft') {
          ctx.fillRect(sx + PT_PX, sy, sw - PT_PX, PT_PX);
          ctx.strokeRect(sx + PT_PX, sy, sw - PT_PX, PT_PX);
          ctx.fillRect(sx, sy + PT_PX, PT_PX, sh - PT_PX);
          ctx.strokeRect(sx, sy + PT_PX, PT_PX, sh - PT_PX);
        } else if (c === 'topRight') {
          ctx.fillRect(sx, sy, sw - PT_PX, PT_PX);
          ctx.strokeRect(sx, sy, sw - PT_PX, PT_PX);
          ctx.fillRect(sx + sw - PT_PX, sy + PT_PX, PT_PX, sh - PT_PX);
          ctx.strokeRect(sx + sw - PT_PX, sy + PT_PX, PT_PX, sh - PT_PX);
        } else if (c === 'bottomLeft') {
          ctx.fillRect(sx + PT_PX, sy + sh - PT_PX, sw - PT_PX, PT_PX);
          ctx.strokeRect(sx + PT_PX, sy + sh - PT_PX, sw - PT_PX, PT_PX);
          ctx.fillRect(sx, sy, PT_PX, sh - PT_PX);
          ctx.strokeRect(sx, sy, PT_PX, sh - PT_PX);
        } else if (c === 'bottomRight') {
          ctx.fillRect(sx, sy + sh - PT_PX, sw - PT_PX, PT_PX);
          ctx.strokeRect(sx, sy + sh - PT_PX, sw - PT_PX, PT_PX);
          ctx.fillRect(sx + sw - PT_PX, sy, PT_PX, sh - PT_PX);
          ctx.strokeRect(sx + sw - PT_PX, sy, PT_PX, sh - PT_PX);
        }
      }
    });

    // 칸막이 렌더링 (restore 이후 — tx/ty 직접 더함)
    middleWalls.forEach(mw => {
      const dmw = print ? null : draggingMW;
      const mwPos = (dmw && dmw.id === mw.id) ? dmw.position : mw.position;
      const isDraggingMW = !!(dmw && dmw.id === mw.id);

      // 칸막이 중심선 좌표 (도면 기준)
      const mwCenter = panelThick + mwPos;
      const mwThick = mw.panelThick;
      const mwThickPx = mwThick * scale;

      // 화면 픽셀 좌표
      const innerH = horizontal - panelThick * 2;
      const innerV = vertical - panelThick * 2;

      if (mw.direction === 'vertical') {
        // 세로 칸막이: x=mwCenter, y=panelThick ~ vertical-panelThick
        const sx = tx + PADDING + mwCenter * scale;
        const sy1 = ty + PADDING + panelThick * scale;
        const sy2 = ty + PADDING + (vertical - panelThick) * scale;
        const mwLen = innerV;
        const mwLenPx = mwLen * scale;

        // 칸막이 박스 (새 엔진이면 끌어서 옮기는 동안만 안내용으로)
        if (!poly || isDraggingMW) {
        ctx.fillStyle = isDraggingMW ? '#E8EAF6' : '#EDE7F6';
        ctx.fillRect(sx - mwThickPx / 2, sy1, mwThickPx, mwLenPx);
        ctx.strokeStyle = isDraggingMW ? '#3949AB' : '#5C6BC0';
        ctx.lineWidth = isDraggingMW ? 2 : 1.5;
        ctx.strokeRect(sx - mwThickPx / 2, sy1, mwThickPx, mwLenPx);
        }

        // 패널 분할 렌더링 (새 엔진이면 엔진 패널로 그림 — 생략)
        if (!poly) {
        const mwPanels = calculateMiddleWallPanels(mwLen, config, mw.panelVariant ?? 1);
        const TD_MW = mwThickPx * 0.45;
        mwPanels.forEach(mp => {
          const psy = sy1 + mp.pos * scale;
          const psh = mp.size * scale;
          const centerY = psy + psh / 2;
          ctx.save();
          ctx.translate(sx, centerY);
          ctx.rotate(-Math.PI / 2);
          ctx.translate(-psh / 2, -mwThickPx / 2);

          // 본체
          ctx.fillStyle = '#FFF'; ctx.fillRect(0, 0, psh, mwThickPx);
          ctx.strokeStyle = '#5C6BC0'; ctx.lineWidth = 1; ctx.strokeRect(0, 0, psh, mwThickPx);

          // -π/2 회전 시 로컬 left(x=0)→화면 아래, right(x=psh)→화면 위 → swap
          const ltSwap = mp.rightType;
          const rtSwap = mp.leftType;

          // 왼쪽 끝 (화면에서 위쪽)
          if (ltSwap !== 'none') {
            ctx.beginPath();
            if (ltSwap === 'yangsu') {
              ctx.moveTo(0, 0); ctx.lineTo(-TD_MW, mwThickPx / 2); ctx.lineTo(0, mwThickPx);
            } else if (ltSwap === 'eumsu') {
              ctx.moveTo(0, 0); ctx.lineTo(TD_MW, mwThickPx / 2); ctx.lineTo(0, mwThickPx);
            }
            ctx.closePath(); ctx.fillStyle = '#FFF'; ctx.fill(); ctx.strokeStyle = '#5C6BC0'; ctx.stroke();
          }
          // 오른쪽 끝 (화면에서 아래쪽)
          if (rtSwap !== 'none') {
            ctx.beginPath();
            if (rtSwap === 'yangsu') {
              ctx.moveTo(psh, 0); ctx.lineTo(psh + TD_MW, mwThickPx / 2); ctx.lineTo(psh, mwThickPx);
            } else if (rtSwap === 'eumsu') {
              ctx.moveTo(psh, 0); ctx.lineTo(psh - TD_MW, mwThickPx / 2); ctx.lineTo(psh, mwThickPx);
            }
            ctx.closePath(); ctx.fillStyle = '#FFF'; ctx.fill(); ctx.strokeStyle = '#5C6BC0'; ctx.stroke();
          }

          // 사이즈 라벨
          const fontSize = Math.max(8, Math.min(FONT_SIZE, mwThickPx * 0.4));
          if (psh > 20 && mwThickPx > 10) {
            ctx.fillStyle = '#5C6BC0'; ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(`${mp.size}`, psh / 2, mwThickPx / 2);
          }
          ctx.restore();
        });
        }

        // 위치 표시 (좌측 벽 안쪽에서 거리)
        const distLeft = Math.round(mwPos);
        const wallLeftPx = tx + PADDING + panelThick * scale;
        const midY = (sy1 + sy2) / 2;
        ctx.strokeStyle = isDraggingMW ? '#3949AB' : '#7986CB';
        ctx.lineWidth = 0.8; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(wallLeftPx, midY); ctx.lineTo(sx, midY); ctx.stroke();
        ctx.setLineDash([]);
        ctx.font = 'bold 10px Arial';
        const distLabel = `${distLeft}`;
        const tw = ctx.measureText(distLabel).width;
        const bw = tw + 10, bh = 16;
        const lx = (wallLeftPx + sx) / 2;
        ctx.fillStyle = '#E8EAF6'; ctx.fillRect(lx - bw / 2, midY - bh / 2, bw, bh); otherBoxes.push({ x: lx - bw / 2, y: midY - bh / 2, w: bw, h: bh });
        ctx.strokeStyle = '#5C6BC0'; ctx.lineWidth = 1; ctx.strokeRect(lx - bw / 2, midY - bh / 2, bw, bh);
        ctx.fillStyle = '#3949AB'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(distLabel, lx, midY);

      } else {
        // 가로 칸막이: y=mwCenter, x=panelThick ~ horizontal-panelThick
        const sy = ty + PADDING + mwCenter * scale;
        const sx1 = tx + PADDING + panelThick * scale;
        const sx2 = tx + PADDING + (horizontal - panelThick) * scale;
        const mwLen = innerH;
        const mwLenPx = mwLen * scale;

        // 칸막이 박스 (새 엔진이면 끌어서 옮기는 동안만 안내용으로)
        if (!poly || isDraggingMW) {
        ctx.fillStyle = isDraggingMW ? '#E8EAF6' : '#EDE7F6';
        ctx.fillRect(sx1, sy - mwThickPx / 2, mwLenPx, mwThickPx);
        ctx.strokeStyle = isDraggingMW ? '#3949AB' : '#5C6BC0';
        ctx.lineWidth = isDraggingMW ? 2 : 1.5;
        ctx.strokeRect(sx1, sy - mwThickPx / 2, mwLenPx, mwThickPx);
        }

        // 패널 분할 렌더링 (새 엔진이면 엔진 패널로 그림 — 생략)
        if (!poly) {
        const mwPanels = calculateMiddleWallPanels(mwLen, config, mw.panelVariant ?? 1);
        const TD_MW = mwThickPx * 0.45;
        mwPanels.forEach(mp => {
          const psx = sx1 + mp.pos * scale;
          const psw = mp.size * scale;
          const centerX = psx + psw / 2;
          ctx.save();
          ctx.translate(centerX, sy);
          ctx.translate(-psw / 2, -mwThickPx / 2);

          // 본체
          ctx.fillStyle = '#FFF'; ctx.fillRect(0, 0, psw, mwThickPx);
          ctx.strokeStyle = '#5C6BC0'; ctx.lineWidth = 1; ctx.strokeRect(0, 0, psw, mwThickPx);

          // 왼쪽 끝
          if (mp.leftType !== 'none') {
            ctx.beginPath();
            if (mp.leftType === 'yangsu') {
              ctx.moveTo(0, 0); ctx.lineTo(-TD_MW, mwThickPx / 2); ctx.lineTo(0, mwThickPx);
            } else if (mp.leftType === 'eumsu') {
              ctx.moveTo(0, 0); ctx.lineTo(TD_MW, mwThickPx / 2); ctx.lineTo(0, mwThickPx);
            }
            ctx.closePath(); ctx.fillStyle = '#FFF'; ctx.fill(); ctx.strokeStyle = '#5C6BC0'; ctx.stroke();
          }
          // 오른쪽 끝
          if (mp.rightType !== 'none') {
            ctx.beginPath();
            if (mp.rightType === 'yangsu') {
              ctx.moveTo(psw, 0); ctx.lineTo(psw + TD_MW, mwThickPx / 2); ctx.lineTo(psw, mwThickPx);
            } else if (mp.rightType === 'eumsu') {
              ctx.moveTo(psw, 0); ctx.lineTo(psw - TD_MW, mwThickPx / 2); ctx.lineTo(psw, mwThickPx);
            }
            ctx.closePath(); ctx.fillStyle = '#FFF'; ctx.fill(); ctx.strokeStyle = '#5C6BC0'; ctx.stroke();
          }

          // 사이즈 라벨
          const fontSize = Math.max(8, Math.min(FONT_SIZE, mwThickPx * 0.4));
          if (psw > 20 && mwThickPx > 10) {
            ctx.fillStyle = '#5C6BC0'; ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(`${mp.size}`, psw / 3, mwThickPx / 2);
          }
          ctx.restore();
        });
        }

        // 위치 표시 (상단 벽 안쪽에서 거리) — 좌측 1/4 지점에 표시해 천장 라벨과 겹침 방지
        const distTop = Math.round(mwPos);
        const wallTopPx = ty + PADDING + panelThick * scale;
        const lineX = sx1 + (sx2 - sx1) / 4;
        ctx.strokeStyle = isDraggingMW ? '#3949AB' : '#7986CB';
        ctx.lineWidth = 0.8; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(lineX, wallTopPx); ctx.lineTo(lineX, sy); ctx.stroke();
        ctx.setLineDash([]);
        ctx.font = 'bold 10px Arial';
        const distLabel = `${distTop}`;
        const tw = ctx.measureText(distLabel).width;
        const bw = tw + 10, bh = 16;
        const ly = (wallTopPx + sy) / 2;
        ctx.fillStyle = '#E8EAF6'; ctx.fillRect(lineX - bw / 2, ly - bh / 2, bw, bh); otherBoxes.push({ x: lineX - bw / 2, y: ly - bh / 2, w: bw, h: bh });
        ctx.strokeStyle = '#5C6BC0'; ctx.lineWidth = 1; ctx.strokeRect(lineX - bw / 2, ly - bh / 2, bw, bh);
        ctx.fillStyle = '#3949AB'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(distLabel, lineX, ly);
      }
    });

    // 천장 패널 사이즈 라벨 (칸막이 위에 표시 — 1/3, 2/3 두 지점)
    ctx.save(); clipToOutline(tx, ty);
    if (ceilingPanels.length > 0) {
      ctx.fillStyle = '#333'; ctx.font = `bold ${FONT_SIZE}px "Malgun Gothic", Arial, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      // 글자 상자가 실내(외곽선 안, 벽 패널 띠 밖)에 온전히 들어가는가 — 잘라낸 모양·하리 홈에서 벽 글자와 겹치지 않게
      const ol = poly?.outline ?? [{ x: 0, y: 0 }, { x: horizontal, y: 0 }, { x: horizontal, y: vertical }, { x: 0, y: vertical }];
      const inOutline = (x: number, y: number) => {
        let c = false;
        for (let i = 0, j = ol.length - 1; i < ol.length; j = i++) {
          const a = ol[i], b = ol[j];
          if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) c = !c;
        }
        return c;
      };
      // 벽·칸막이 패널 띠 (패널 a→b는 띠 가운데 선) — 글자 상자가 닿으면 안 됨
      const bands = (poly?.panels ?? []).map(p => {
        const h = panelThick / 2;
        return [Math.min(p.a.x, p.b.x) - h, Math.min(p.a.y, p.b.y) - h, Math.max(p.a.x, p.b.x) + h, Math.max(p.a.y, p.b.y) + h];
      });
      const fits = (x: number, y: number, hw: number, hh: number) => inOutline(x, y) && ol.every((a, i) => {
        const b = ol[(i + 1) % ol.length];
        if (a.y === b.y) return x + hw < Math.min(a.x, b.x) || x - hw > Math.max(a.x, b.x) || Math.abs(y - a.y) >= panelThick + hh;
        return y + hh < Math.min(a.y, b.y) || y - hh > Math.max(a.y, b.y) || Math.abs(x - a.x) >= panelThick + hw;
      }) && bands.every(([x0, y0, x1, y1]) => x + hw <= x0 || x - hw >= x1 || y + hh <= y0 || y - hh >= y1)
        // 배수구 거리·칸막이 위치 글자(화면 좌표 상자)와도 겹치지 않게
        && [...newLabels, ...otherBoxes].every(l => {
          const px = tx + PADDING + x * scale, py = ty + PADDING + y * scale, w = hw * scale, h = hh * scale;
          return px + w <= l.x || px - w >= l.x + l.w || py + h <= l.y || py - h >= l.y + l.h;
        });
      ceilingPanels.forEach(cp => {
        const label = panelNos ? `C${String(cp.index + 1).padStart(2, '0')}` : `${cp.size}`;
        if (cp.size * scale < 14) return; // 칸이 좁으면(축소 화면) 라벨 생략 — 겹침 방지
        const horiz = ceilingDirection === 'H';
        const hw = (ctx.measureText(label).width / 2 + 3) / scale, hh = (FONT_SIZE / 2 + 2) / scale; // 글자 상자 반 크기(mm)
        // 줄 가운데 (H면 y, V면 x) — 가운데가 칸막이 등에 막혀 한 자리도 없으면 줄 안에서 위아래(좌우)로 비켜 봄
        const tryMid = (m: number) => (t: number) => (horiz ? fits(t, m, hw, hh) : fits(m, t, hh, hw));
        const anyAt = (m: number) => Array.from({ length: 41 }, (_, k) => (cp.length * k) / 40).some(tryMid(m));
        const mid = [0, -0.22, 0.22, -0.32, 0.32].map(f => cp.pos + cp.size * (0.5 + f)).find(anyAt);
        if (mid === undefined) return;
        const at = tryMid(mid);
        // 기본: 1/3·2/3 지점, 둘 다 들어가면 그대로
        let spots = [cp.length / 3, cp.length * 2 / 3];
        if (!spots.every(at)) {
          // 줄을 촘촘히 훑어 글자가 들어가는 구간(칸막이·홈으로 나뉜 방)마다 가운데에 하나 — 줄 절반보다 긴 방은 두 개
          const N = 120, runs: [number, number][] = [];
          let start: number | null = null;
          for (let k = 0; k <= N; k++) {
            const t = (cp.length * k) / N, ok = at(t);
            if (ok && start === null) start = t;
            if ((!ok || k === N) && start !== null) { runs.push([start, ok ? t : (cp.length * (k - 1)) / N]); start = null; }
          }
          spots = [];
          runs.forEach(([a, b]) => {
            const L = b - a;
            if (L > cp.length / 2 && L > hw * 8) spots.push(a + L / 3, a + L * 2 / 3);
            else spots.push(a + L / 2);
          });
          if (!spots.length) return;
        }
        spots.forEach(t => {
          if (horiz) ctx.fillText(label, tx + PADDING + t * scale, ty + PADDING + mid * scale);
          else ctx.fillText(label, tx + PADDING + mid * scale, ty + PADDING + t * scale);
        });
      });
    }
    ctx.restore(); // 천장 라벨 잘라내기 해제

    // 범례
    const legendX = cw - 200, legendY = ch - 70;
    ctx.font = '11px Arial'; ctx.textAlign = 'left';
    ctx.fillStyle = '#C8E6C9'; ctx.fillRect(legendX, legendY, 14, 10);
    ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 1; ctx.strokeRect(legendX, legendY, 14, 10);
    ctx.fillStyle = '#333'; ctx.fillText('문 (Door)', legendX + 20, legendY + 9);
    ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(legendX + 7, legendY + 23, 5, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#333'; ctx.fillText('배수구 (Drain)', legendX + 20, legendY + 27);
    if (!print) {
      ctx.fillStyle = '#999'; ctx.font = '11px Arial';
      ctx.fillText(`${Math.round(zoom * 100)}%`, 10, ch - 10);
    }

  }, [horizontal, vertical, walls, config, panelThick, doors, drains, zoom, viewOffset, getBaseScale, ceilingPanels, ceilingDirection, draggingDrain, middleWalls, draggingMW, haris, poly, selectedPolyId, existingWalls, doorEnds, doorGuide, panelNos]);

  // 화면 그리기
  useEffect(() => {
    const canvas = canvasRef.current, container = containerRef.current;
    if (!canvas || !container) return;
    renderScene(canvas, container.clientWidth, container.clientHeight, window.devicePixelRatio || 1);
  }, [renderScene, sizeTick]);

  // 인쇄용: 화면과 같은 그림을 원하는 크기(css px)로 고해상도 이미지로
  useEffect(() => {
    if (!exportRef) return;
    exportRef.current = (w: number, h: number) => {
      const c = document.createElement('canvas');
      renderScene(c, w, h, 3, true);
      return c;
    };
    return () => { exportRef.current = null; };
  }, [exportRef, renderScene]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // 크기가 바뀌면(사이드바 접기·창 크기·패드 회전) 다시 그림 — 같은 값 setZoom은 다시 그리지 않아서 따로 신호
    const observer = new ResizeObserver(() => setSizeTick(t => t + 1));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const getCursor = () => {
    if (draggingDrain) return 'grabbing';
    if (draggingMW) return 'grabbing';
    if (isPanning) return 'grabbing';
    if (activeTool === 'door') return hoverWall ? 'copy' : 'default';
    if (doorGuide) return horizOrVert(doorGuide.side);
    if (activeTool === 'drain') return hoverWall ? 'copy' : 'default';
    if (activeTool === 'delete') return 'not-allowed';
    if (activeTool === 'select') return 'grab';
    return 'default';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0 8px', flexShrink: 0 }}>
        <button className="pd-chip" onClick={() => setZoom(prev => Math.max(0.2, prev - 0.2))} style={{ width: 30, padding: 0, justifyContent: 'center', fontSize: 16 }}>−</button>
        <input type="range" min="20" max="500" value={zoom * 100}
          onChange={e => setZoom(Number(e.target.value) / 100)} style={{ width: 120, accentColor: '#3182F6' }} />
        <button className="pd-chip" onClick={() => setZoom(prev => Math.min(5, prev + 0.2))} style={{ width: 30, padding: 0, justifyContent: 'center', fontSize: 16 }}>+</button>
        <span style={{ fontSize: 13, color: 'var(--pd-mute)', minWidth: 44, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button className="pd-chip" onClick={resetZoom}>화면 맞춤</button>
      </div>
      <div ref={containerRef}
        style={{
          width: '100%', flex: 1, overflow: 'hidden',
          borderRadius: 12, background: '#fff', border: '1px solid var(--pd-line)',
          cursor: getCursor(), userSelect: 'none', position: 'relative', touchAction: 'none', WebkitUserSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { setIsPanning(false); }}
      >
        <canvas ref={canvasRef} />

        {/* 거리 입력 팝업 */}
        {drainDistEdit && (
          <DrainDistPopup
            edit={drainDistEdit}
            onConfirm={(val) => {
              const d = drainDistEdit;
              // 패널 안쪽 면 기준 역산
              let newX = drains.find(dr => dr.id === d.drainId)?.x ?? 0;
              let newY = drains.find(dr => dr.id === d.drainId)?.y ?? 0;
              if (d.dir === 'top')    newY = panelThick + val;
              if (d.dir === 'bottom') newY = (vertical - panelThick) - val;
              if (d.dir === 'left')   newX = panelThick + val;
              if (d.dir === 'right')  newX = (horizontal - panelThick) - val;
              onDrainMove?.(d.drainId, Math.round(newX), Math.round(newY));
              setDrainDistEdit(null);
            }}
            onCancel={() => setDrainDistEdit(null)}
          />
        )}
      </div>
    </div>
  );
};


// 거리 입력 팝업 (인라인 컴포넌트)
const dirNames: Record<string, string> = { top: '상단 벽', bottom: '하단 벽', left: '좌측 벽', right: '우측 벽' };
const DrainDistPopup: React.FC<{
  edit: { drainId: string; dir: string; current: number; clientX: number; clientY: number };
  onConfirm: (val: number) => void;
  onCancel: () => void;
}> = ({ edit, onConfirm, onCancel }) => {
  const [val, setVal] = React.useState(edit.current);
  return (
    <div style={{
      position: 'absolute', left: edit.clientX - 100, top: edit.clientY - 80,
      background: '#fff', border: '1px solid #1565C0', borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)', padding: '12px 16px',
      zIndex: 2000, minWidth: 200,
    }}>
      <div style={{ fontSize: 12, fontWeight: 'bold', color: '#1565C0', marginBottom: 8 }}>
        {dirNames[edit.dir]}까지 거리 (mm)
      </div>
      <input
        type="number" value={val} step={10}
        onChange={e => setVal(Number(e.target.value))}
        onKeyDown={e => { if (e.key === 'Enter') onConfirm(val); if (e.key === 'Escape') onCancel(); }}
        autoFocus
        style={{ width: '100%', padding: '6px 8px', fontSize: 14, border: '1px solid #1565C0', borderRadius: 4, boxSizing: 'border-box' as const, marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '4px 12px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>취소</button>
        <button onClick={() => onConfirm(val)} style={{ padding: '4px 12px', fontSize: 12, border: 'none', borderRadius: 4, background: '#1565C0', color: '#fff', cursor: 'pointer' }}>적용</button>
      </div>
    </div>
  );
};

export default DrawingCanvas;
